<template>
  <q-dialog v-model="shown">
    <q-card style="width: 500px;">
      <q-card-section class="row items-center q-mx-xs">
        <div class="text-h6">
          Food Ordering QR Code Generator
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section>
        <div class="row q-gutter-sm q-mb-md">
          <q-select
            v-model="deliveryType"
            class="col"
            label="Delivery Type"
            emit-value
            map-options
            :options="deliveryTypeOptions"
          />
          <q-select
            v-if="$_.includes(DELIVERY_TYPE_OPTIONS.DELIVERY_LOCATION_REQUIRED, deliveryType)"
            v-model="deliveryLocation"
            class="col"
            label="Delivery Location"
            :options="deliveryLocationOptions"
            option-label="name"
            :use-input="!deliveryLocation"
            input-debounce="50"
            @filter="filterDeliveryLocationOptions"
          />
          <q-select
            v-if="deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT"
            v-model="deliveryPosition"
            class="col"
            label="Delivery Position"
            :options="deliveryPositionOptions"
            :use-input="!deliveryPosition"
            input-debounce="50"
            @filter="filterDeliveryPositionOptions"
          />
          <q-input
            v-if="customDeliveryType && customDeliveryType.hasDetails"
            v-model="deliveryDetails"
            class="col-12"
            :label="customDeliveryType.detailsFieldName"
          />
        </div>
        <q-input
          v-model="url"
          label="Url"
          class="full-width q-mb-md"
        />
        <div class="row justify-center">
          <qr-code
            class="qr-code col-auto"
            color="black"
            bg-color="white"
            :style="'border: solid 10px white'"
            :text="url"
          />
        </div>
        <div class="text-center">
          {{ $_.startCase(deliveryTypeName) }}{{ location }}{{ details }}
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>

import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import VueQRCodeComponent from 'vue-qr-generator'

export default {
  name: 'OnlineQrCodeGeneratorDialog',
  components: {
    'qr-code': VueQRCodeComponent
  },
  configuration: ['currentSite', 'deliveryTypes', 'deliveryLocations'],
  props: {
    modelValue: {
      type: Boolean,
      required: false
    }
  },
  emits: ['update:model-value'],
  data () {
    return {
      shown: false,
      deliveryType: DELIVERY_TYPE_OPTIONS.TABLE,
      allDeliveryLocationOptions: [],
      deliveryLocationOptions: [],
      allDeliveryPositionOptions: [],
      deliveryPositionOptions: [],
      deliveryLocation: (this.deliveryLocations?.data?.length && this.deliveryLocations.data[0]),
      deliveryPosition: (this.deliveryLocations?.data?.length && this.deliveryLocations.data[0].positions.length && this.deliveryLocations.data[0].positions[0]),
      deliveryDetails: '',
      url: ''
    }
  },
  computed: {
    deliveryTypeOptions () {
      return _.concat(this.$mh.asOptions(DELIVERY_TYPE_OPTIONS.DELIVERY_LOCATION_REQUIRED), _.map(
        _.filter(this.deliveryTypes?.data, deliveryType => {
          return deliveryType.enable && deliveryType.availableOnline
        }),
        deliveryType => {
          return { label: deliveryType.name, value: deliveryType.id.toString() }
        }))
    },
    customDeliveryType () {
      if (!Number.isInteger(Number.parseInt(this.deliveryType))) return null

      return _.find(this.deliveryTypes?.data, deliveryType => deliveryType.id === Number.parseInt(this.deliveryType))
    },
    deliveryTypeName () {
      return this.customDeliveryType?.name || this.deliveryType
    },
    location () {
      if (this.deliveryType === DELIVERY_TYPE_OPTIONS.TABLE && this.deliveryLocation) {
        return ` - ${this.deliveryLocation?.name}`
      } else if (this.deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT && this.deliveryLocation) {
        return ` - ${this.deliveryLocation?.name} - ${this.deliveryPosition}`
      }

      return ''
    },
    details () {
      if (this.customDeliveryType?.hasDetails && this.deliveryDetails?.length) {
        return ` - ${this.deliveryDetails}`
      }

      return ''
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
      this.validateUrl()
    },
    shown () {
      this.$emit('update:model-value', this.shown)
    },
    deliveryType: {
      async handler () {
        if (this.deliveryLocations && this.deliveryLocations.data) {
          this.allDeliveryLocationOptions = _.sortBy(_.filter(this.deliveryLocations.data, { type: (this.deliveryType || DELIVERY_TYPE_OPTIONS.TABLE) }), 'displayOrder')

          if ((this.deliveryType || DELIVERY_TYPE_OPTIONS.TABLE) !== this.deliveryLocation?.type) this.deliveryLocation = this.allDeliveryLocationOptions[0]
        } else {
          this.allDeliveryLocationOptions = []
        }

        await this.$nextTick()
        this.computeDeliveryPositionOptions()
        await this.$nextTick()
        this.validateUrl()
      },
      immediate: true
    },
    deliveryLocation: {
      async handler () {
        this.computeDeliveryPositionOptions()
        await this.$nextTick()
        this.validateUrl()
      },
      immediate: true
    },
    deliveryDetails () {
      this.validateUrl()
    }
  },
  created () {
    _.extend(this, { DELIVERY_TYPE_OPTIONS })
  },
  methods: {
    show () {
      this.value = true
    },
    validateUrl () {
      let location = ''
      if (_.includes(DELIVERY_TYPE_OPTIONS.DELIVERY_LOCATION_REQUIRED, this.deliveryType)) location += `&delivery-location=${encodeURIComponent(this.deliveryLocation?.name)}`
      if (this.deliveryType === DELIVERY_TYPE_OPTIONS.THEATER_SEAT) location += `&delivery-position=${encodeURIComponent(this.deliveryPosition)}`

      let details = ''
      if (this.customDeliveryType?.hasDetails && this.deliveryDetails?.length) details += `&delivery-details=${encodeURIComponent(this.deliveryDetails)}`

      this.url = this.currentSite.url(`/checkout/items?delivery-type=${this.deliveryType}${location}${details}`)
    },
    filterDeliveryLocationOptions (value, update) {
      if (this.allDeliveryLocationOptions) {
        if (value === '') {
          update(() => {
            this.deliveryLocationOptions = this.allDeliveryLocationOptions
          })
        } else {
          update(() => {
            this.deliveryLocationOptions = _.filter(this.allDeliveryLocationOptions, deliveryLocation => {
              return deliveryLocation.name.includes(value)
            })
          })
        }
      }
    },
    computeDeliveryPositionOptions () {
      if (this.deliveryLocation && this.deliveryLocation.positions) {
        if (!this.deliveryPosition || (this.deliveryPosition && !_.includes(this.deliveryLocation.positions, this.deliveryPosition))) {
          this.deliveryPosition = this.deliveryLocation.positions[0]
        }

        this.allDeliveryPositionOptions = this.deliveryLocation.positions
      } else {
        this.deliveryPosition = ''
        this.allDeliveryPositionOptions = []
      }
    },
    filterDeliveryPositionOptions (value, update) {
      if (this.allDeliveryPositionOptions) {
        if (value === '') {
          update(() => {
            this.deliveryPositionOptions = this.allDeliveryPositionOptions
          })
        } else {
          update(() => {
            this.deliveryPositionOptions = _.filter(this.allDeliveryPositionOptions, deliveryPosition => {
              return deliveryPosition.includes(value)
            })
          })
        }
      }
    }
  }
}

</script>

<style lang="scss" scoped>
</style>
